import type { FetchServicesResponse } from "@/services/booking";

/**
 * Get IV nutrients service IDs dynamically from API data
 * Filters services from the "IV Therapy / IM Shots" category
 *
 * The function filters out:
 * - Consultations (containing "consultation" in name)
 * - Assessments (containing "assessment" in name)
 * - IM Shots (containing "im shot" or "shot" in name)
 *
 * Only actual IV drip services that consume nutrients are included.
 */
export function getIVNutrientsServiceIds(
  servicesData: FetchServicesResponse,
): number[] {
  const ivTherapyCategory = servicesData.find(
    (category) => category.category === "IV Therapy / IM Shots",
  );

  if (!ivTherapyCategory) {
    return [];
  }

  // Filter out consultations, assessments, and IM shots
  // Only include actual IV drip services that consume nutrients
  return ivTherapyCategory.services
    .filter((service) => {
      const name = service.name.toLowerCase();
      const isConsultation =
        name.includes("consultation") || name.includes("assessment");
      const isIMShot = name.includes("im shot") || name.includes("shot");

      // Exclude consultations, assessments, and IM shots
      return !isConsultation && !isIMShot;
    })
    .map((service) => service.id);
}

/**
 * Check if a service is an IV nutrients service using dynamic data
 *
 * This replaces the previous isIVNutrientsService function that used hardcoded IDs.
 * Now it dynamically determines IV nutrients services from the API response.
 */
export function isIVNutrientsServiceDynamic(
  serviceId: number | string,
  servicesData: FetchServicesResponse,
): boolean {
  const numericServiceId =
    typeof serviceId === "string" ? parseInt(serviceId, 10) : serviceId;

  const ivNutrientsIds = getIVNutrientsServiceIds(servicesData);
  return ivNutrientsIds.includes(numericServiceId);
}
